import React, { useState, useMemo, useCallback, useRef } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faScaleBalanced, faWallet, faCoins, faCrown, faPlus, faTrash, faCopy,
  faRotate, faCalendarDays, faSackDollar, faEquals, faCircleInfo,
} from '@fortawesome/free-solid-svg-icons';
import { UiPageHeader, UiFilterPanel, UiStatsGrid } from '../../components/ui';
import type { StatCardDef } from '../../components/ui';

dayjs.extend(utc);
dayjs.extend(timezone);
const TZ = 'Asia/Bangkok';

const DATE_PICKER_CLASS =
  'w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';

const PRESET_BTN_CLASS =
  'px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 hover:border-gray-400 dark:hover:border-slate-500 text-gray-600 dark:text-slate-300 transition-colors';

const MONEY_INPUT_CLASS =
  'w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm text-right font-semibold tabular-nums focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';

const PRESETS = [
  { label: 'วันนี้',     from: () => dayjs().tz(TZ).startOf('day'),                       to: () => dayjs().tz(TZ).endOf('day') },
  { label: 'เมื่อวาน',   from: () => dayjs().tz(TZ).subtract(1, 'day').startOf('day'),    to: () => dayjs().tz(TZ).subtract(1, 'day').endOf('day') },
  { label: '7 วัน',      from: () => dayjs().tz(TZ).subtract(6, 'day').startOf('day'),    to: () => dayjs().tz(TZ).endOf('day') },
  { label: '30 วัน',     from: () => dayjs().tz(TZ).subtract(29, 'day').startOf('day'),   to: () => dayjs().tz(TZ).endOf('day') },
  { label: 'เดือนนี้',   from: () => dayjs().tz(TZ).startOf('month'),                     to: () => dayjs().tz(TZ).endOf('day') },
  { label: 'เดือนก่อน', from: () => dayjs().tz(TZ).subtract(1, 'month').startOf('month'), to: () => dayjs().tz(TZ).subtract(1, 'month').endOf('month') },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (v: number) =>
  v.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const parseNum = (s: string): number => {
  const n = parseFloat(String(s ?? '').replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : 0;
};

interface CommissionRow {
  id: number;
  name: string;
  amount: string;
}

interface WalletValues {
  deposit: string;   // กระเป๋าฝาก (รวมทุก merchant)
  withdraw: string;  // กระเป๋าถอน (รวมทุก merchant)
  safe: string;      // Safe Wallet (รวมทุก merchant)
  reseller: string;  // Wallet reseller รวม
  usdt: string;      // Total USDT
  ownerFee: string;  // ส่วนเจ้าของจาก fee-report
}

const EMPTY_VALUES: WalletValues = { deposit: '', withdraw: '', safe: '', reseller: '', usdt: '', ownerFee: '' };

// ─── Component ───────────────────────────────────────────────────────────────

const SettlementCalcPage: React.FC = () => {
  const [draftStartDate, setDraftStartDate] = useState<Date>(() => dayjs().tz(TZ).startOf('month').toDate());
  const [draftEndDate, setDraftEndDate] = useState<Date>(() => dayjs().tz(TZ).endOf('day').toDate());
  const [appliedStartDate, setAppliedStartDate] = useState<Date | null>(null);
  const [appliedEndDate, setAppliedEndDate] = useState<Date | null>(null);

  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);

  const [values, setValues] = useState<WalletValues>(EMPTY_VALUES);
  const [commissions, setCommissions] = useState<CommissionRow[]>([{ id: 1, name: '', amount: '' }]);
  const nextRowId = useRef(2);

  const setValue = (key: keyof WalletValues, v: string) =>
    setValues(prev => ({ ...prev, [key]: v }));

  // ─── ดึงข้อมูลจาก 4 แหล่งพร้อมกัน (คำนวณอย่างเดียว ไม่บันทึกอะไรทั้งนั้น) ───
  const fetchAll = useCallback(async (from: Date, to: Date) => {
    setLoading(true);
    const params = { date_from: dayjs(from).toISOString(), date_to: dayjs(to).toISOString() };

    const [merchantsRes, resellersRes, usdtRes, feeRes] = await Promise.allSettled([
      merchantApi.get('/merchants'),
      merchantApi.get('/admin/resellers'),
      merchantApi.get('/admin/usdt-wallets/dashboard'),
      merchantApi.get('/admin/fee-report', { params }),
    ]);

    const failed: string[] = [];
    const next: WalletValues = { ...EMPTY_VALUES };

    if (merchantsRes.status === 'fulfilled') {
      const merchants: any[] = merchantsRes.value.data.data || [];
      next.deposit  = merchants.reduce((s, m) => s + (Number(m.wallet?.balance) || 0), 0).toFixed(2);
      next.withdraw = merchants.reduce((s, m) => s + (Number(m.wallet?.payout_balance) || 0), 0).toFixed(2);
      next.safe     = merchants.reduce((s, m) => s + (Number(m.wallet?.safe_balance) || 0), 0).toFixed(2);
    } else {
      failed.push('Merchants');
    }

    if (resellersRes.status === 'fulfilled') {
      const resellers: any[] = resellersRes.value.data.data || [];
      next.reseller = resellers.reduce((s, r) => s + (Number(r.wallet?.balance) || 0), 0).toFixed(2);
    } else {
      failed.push('Resellers');
    }

    if (usdtRes.status === 'fulfilled') {
      next.usdt = (Number(usdtRes.value.data.data?.total_usdt) || 0).toFixed(2);
    } else {
      failed.push('USDT Wallet');
    }

    if (feeRes.status === 'fulfilled') {
      next.ownerFee = (Number(feeRes.value.data.data?.summary?.total_owner_share) || 0).toFixed(2);
    } else {
      failed.push('Fee Report');
    }

    setValues(next);
    setLoaded(true);
    setLoadedAt(new Date());
    setLoading(false);

    if (failed.length) {
      toast.error(`โหลดข้อมูลไม่สำเร็จ: ${failed.join(', ')} (กรอกเองได้ในช่องที่ว่าง)`);
    } else {
      toast.success('ดึงยอดล่าสุดครบทุกแหล่งแล้ว');
    }
  }, []);

  const handleSearch = () => {
    if (dayjs(draftStartDate).isAfter(dayjs(draftEndDate))) {
      toast.error('วันเริ่มต้นต้องน้อยกว่าหรือเท่ากับวันสิ้นสุด');
      return;
    }
    setAppliedStartDate(draftStartDate);
    setAppliedEndDate(draftEndDate);
    fetchAll(draftStartDate, draftEndDate);
  };

  const applyPreset = (from: () => dayjs.Dayjs, to: () => dayjs.Dayjs) => {
    setDraftStartDate(from().toDate());
    setDraftEndDate(to().toDate());
  };

  // ─── คำนวณสด ────────────────────────────────────────────────────────────────
  const calc = useMemo(() => {
    const deposit  = parseNum(values.deposit);
    const withdraw = parseNum(values.withdraw);
    const safe     = parseNum(values.safe);
    const reseller = parseNum(values.reseller);
    const usdt     = parseNum(values.usdt);
    const ownerFee = parseNum(values.ownerFee);

    const walletTotal = deposit + withdraw + safe + reseller;      // รวมกระเป๋า
    const net = usdt - walletTotal;                                // สุทธิ
    const ownerRemain = ownerFee - net;                            // ส่วนเจ้าของคงเหลือ
    const commissionTotal = commissions.reduce((s, c) => s + parseNum(c.amount), 0); // รวมคอม
    const finalRemain = ownerRemain - commissionTotal;             // คงเหลือสุทธิ์ทั้งหมด

    return { deposit, withdraw, safe, reseller, usdt, ownerFee, walletTotal, net, ownerRemain, commissionTotal, finalRemain };
  }, [values, commissions]);

  // ─── ค่าคอม (กรอกเอง เพิ่ม/ลบได้) ───────────────────────────────────────────
  const addCommission = () =>
    setCommissions(prev => [...prev, { id: nextRowId.current++, name: '', amount: '' }]);

  const removeCommission = (id: number) =>
    setCommissions(prev => (prev.length > 1 ? prev.filter(c => c.id !== id) : prev.map(c => c.id === id ? { ...c, name: '', amount: '' } : c)));

  const updateCommission = (id: number, patch: Partial<CommissionRow>) =>
    setCommissions(prev => prev.map(c => (c.id === id ? { ...c, ...patch } : c)));

  // ─── คัดลอกสรุปเป็นข้อความ (รูปแบบเดียวกับที่ทำมือ) ─────────────────────────
  const copySummary = async () => {
    const c = calc;
    const commLines = commissions
      .filter(r => r.name.trim() || parseNum(r.amount) !== 0)
      .map(r => `ค่าคอมบัญชี ${r.name.trim() || '-'} = ${fmt(parseNum(r.amount))}`);

    const period = appliedStartDate && appliedEndDate
      ? `${dayjs(appliedStartDate).tz(TZ).format('DD/MM/YYYY HH:mm')} – ${dayjs(appliedEndDate).tz(TZ).format('DD/MM/YYYY HH:mm')}`
      : '-';

    const text = [
      `สรุปยอดตัดรอบ (${dayjs().tz(TZ).format('DD/MM/YYYY HH:mm')})`,
      `ช่วง Fee Report: ${period}`,
      '',
      `กระเป๋าฝาก = ${fmt(c.deposit)}`,
      `กระเป๋าถอน = ${fmt(c.withdraw)}`,
      `กระเป๋า Safe Wallet = ${fmt(c.safe)}`,
      `Wallet Reseller รวม = ${fmt(c.reseller)}`,
      `รวมกระเป๋า = ${fmt(c.walletTotal)}`,
      '',
      `ยอดคงเหลือใน Wallet USDT = ${fmt(c.usdt)}`,
      `สุทธิ = ${fmt(c.usdt)} - ${fmt(c.walletTotal)} = ${fmt(c.net)}`,
      '',
      `ส่วนเจ้าของ FEE ตามหลังบ้าน = ${fmt(c.ownerFee)}`,
      `ส่วนเจ้าของคงเหลือ = ${fmt(c.ownerFee)} - ${fmt(c.net)} = ${fmt(c.ownerRemain)}`,
      '',
      ...commLines,
      `รวมคอม = ${fmt(c.commissionTotal)}`,
      '',
      `คงเหลือสุทธิ์ทั้งหมด = ${fmt(c.ownerRemain)} - ${fmt(c.commissionTotal)} = ${fmt(c.finalRemain)}`,
    ].join('\n');

    try {
      await navigator.clipboard.writeText(text);
      toast.success('คัดลอกสรุปยอดแล้ว');
    } catch {
      toast.error('คัดลอกไม่สำเร็จ');
    }
  };

  // ─── Stat cards ──────────────────────────────────────────────────────────────
  const statCards: StatCardDef[] = useMemo(() => [
    {
      icon: faWallet,
      iconClassName: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      label: 'รวมกระเป๋า (ฝาก+ถอน+Safe+Reseller)',
      value: <span className="text-blue-600 dark:text-blue-400">฿{fmt(calc.walletTotal)}</span>,
    },
    {
      icon: faCoins,
      iconClassName: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
      label: 'สุทธิ (USDT − รวมกระเป๋า)',
      value: <span className="text-emerald-600 dark:text-emerald-400">{fmt(calc.net)}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">USDT {fmt(calc.usdt)}</span>,
    },
    {
      icon: faCrown,
      iconClassName: 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
      label: 'ส่วนเจ้าของคงเหลือ (Fee − สุทธิ)',
      value: <span className="text-teal-600 dark:text-teal-400">฿{fmt(calc.ownerRemain)}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">Fee ฿{fmt(calc.ownerFee)}</span>,
    },
    {
      icon: faSackDollar,
      iconClassName: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
      label: 'คงเหลือสุทธิ์ทั้งหมด',
      value: (
        <span className={calc.finalRemain >= 0 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}>
          ฿{fmt(calc.finalRemain)}
        </span>
      ),
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">หักคอม ฿{fmt(calc.commissionTotal)}</span>,
    },
  ], [calc]);

  const moneyField = (
    label: string,
    key: keyof WalletValues,
    source: string,
    accent?: string,
  ) => (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className={`text-xs font-medium ${accent || 'text-gray-500 dark:text-slate-400'}`}>{label}</label>
        <span className="text-[10px] text-gray-400 dark:text-slate-500">{source}</span>
      </div>
      <input
        type="text"
        inputMode="decimal"
        value={values[key]}
        onChange={(e) => setValue(key, e.target.value)}
        placeholder="0.00"
        className={MONEY_INPUT_CLASS}
      />
    </div>
  );

  return (
    <div className="max-w-full overflow-x-hidden">
      <UiPageHeader
        icon={faScaleBalanced}
        title="ตัดยอด (คำนวณสด)"
        subtitle="ดึงยอดจากระบบมาคำนวณอย่างเดียว — ไม่บันทึกข้อมูลใด ๆ"
      />

      <UiFilterPanel
        onSearch={handleSearch}
        loading={loading}
        actions={
          loaded && appliedStartDate && appliedEndDate ? (
            <button
              type="button"
              onClick={() => fetchAll(appliedStartDate, appliedEndDate)}
              disabled={loading}
              className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 hover:bg-gray-50 transition-colors text-gray-600 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-slate-300 flex items-center gap-2 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faRotate} className={loading ? 'animate-spin' : ''} /> ดึงยอดใหม่
            </button>
          ) : undefined
        }
      >
        <div className="flex flex-col gap-3 w-full">
          <div className="flex flex-wrap items-end gap-4 w-fit">
            <div className="w-[200px] shrink-0 z-30">
              <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">ช่วง Fee Report — เริ่มต้น</label>
              <DatePicker
                selected={draftStartDate}
                onChange={(d: Date | null) => d && setDraftStartDate(d)}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={30}
                dateFormat="dd/MM/yyyy HH:mm"
                className={DATE_PICKER_CLASS}
                popperProps={{ strategy: 'fixed' }}
                popperClassName="!z-[9999]"
              />
            </div>
            <div className="w-[200px] shrink-0">
              <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">ช่วง Fee Report — สิ้นสุด</label>
              <DatePicker
                selected={draftEndDate}
                onChange={(d: Date | null) => d && setDraftEndDate(d)}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={30}
                dateFormat="dd/MM/yyyy HH:mm"
                minDate={draftStartDate}
                className={DATE_PICKER_CLASS}
                popperProps={{ strategy: 'fixed' }}
                popperClassName="!z-[9999]"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button key={p.label} type="button" onClick={() => applyPreset(p.from, p.to)} className={PRESET_BTN_CLASS}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </UiFilterPanel>

      {loaded && (
        <>
          <UiStatsGrid cards={statCards} loading={loading} cols={4} />

          {loadedAt && (
            <p className="text-xs text-gray-400 dark:text-slate-500 mb-4 flex items-center gap-1.5">
              <FontAwesomeIcon icon={faCalendarDays} />
              ดึงยอดล่าสุดเมื่อ {dayjs(loadedAt).tz(TZ).format('DD/MM/YYYY HH:mm:ss')}
              {appliedStartDate && appliedEndDate && (
                <> · ช่วง Fee: {dayjs(appliedStartDate).tz(TZ).format('DD/MM/YYYY HH:mm')} – {dayjs(appliedEndDate).tz(TZ).format('DD/MM/YYYY HH:mm')}</>
              )}
            </p>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            {/* ยอดจากระบบ */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-theme border border-gray-100 dark:border-slate-700 p-5">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100 mb-1 flex items-center gap-2">
                <FontAwesomeIcon icon={faWallet} className="text-blue-500" />
                ยอดจากระบบ
              </h3>
              <p className="text-xs text-gray-400 dark:text-slate-500 mb-4">
                ดึงมาให้อัตโนมัติ — แก้ตัวเลขเองได้ทุกช่อง คำนวณใหม่ทันที
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {moneyField('กระเป๋าฝาก', 'deposit', 'จากหน้า Merchants')}
                {moneyField('กระเป๋าถอน', 'withdraw', 'จากหน้า Merchants')}
                {moneyField('กระเป๋า Safe Wallet', 'safe', 'จากหน้า Merchants')}
                {moneyField('Wallet Reseller รวม', 'reseller', 'จากหน้า Resellers')}
                {moneyField('ยอดคงเหลือ Wallet USDT', 'usdt', 'จากหน้า USDT Wallet (Total USDT)', 'text-emerald-600 dark:text-emerald-400')}
                {moneyField('ส่วนเจ้าของ (Fee)', 'ownerFee', 'จาก Fee Report ตามช่วงวันที่', 'text-teal-600 dark:text-teal-400')}
              </div>
            </div>

            {/* ค่าคอมบัญชี */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-theme border border-gray-100 dark:border-slate-700 p-5">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-2">
                  <FontAwesomeIcon icon={faSackDollar} className="text-amber-500" />
                  ค่าคอมบัญชี Bank Agent
                </h3>
                <button
                  type="button"
                  onClick={addCommission}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium text-white flex items-center gap-1.5"
                  style={{ background: 'var(--theme-primary-gradient)' }}
                >
                  <FontAwesomeIcon icon={faPlus} /> เพิ่มบัญชี
                </button>
              </div>
              <p className="text-xs text-gray-400 dark:text-slate-500 mb-4">กรอกเอง — เพิ่ม/ลบได้ตามจำนวนบัญชีในรอบนั้น</p>

              <div className="space-y-2.5">
                {commissions.map((c) => (
                  <div key={c.id} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={c.name}
                      onChange={(e) => updateCommission(c.id, { name: e.target.value })}
                      placeholder="ชื่อบัญชี เช่น TTSC"
                      className="flex-1 px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500"
                    />
                    <input
                      type="text"
                      inputMode="decimal"
                      value={c.amount}
                      onChange={(e) => updateCommission(c.id, { amount: e.target.value })}
                      placeholder="0.00"
                      className={`${MONEY_INPUT_CLASS} w-[150px] flex-none`}
                    />
                    <button
                      type="button"
                      onClick={() => removeCommission(c.id)}
                      className="w-9 h-9 flex-none rounded-lg border border-gray-200 dark:border-slate-600 text-gray-400 hover:text-red-500 hover:border-red-300 dark:hover:border-red-700 transition-colors"
                      title="ลบรายการ"
                    >
                      <FontAwesomeIcon icon={faTrash} className="text-xs" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="mt-4 pt-3 border-t border-gray-100 dark:border-slate-700 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500 dark:text-slate-400">รวมคอม</span>
                <span className="text-base font-bold text-amber-600 dark:text-amber-400 tabular-nums">฿{fmt(calc.commissionTotal)}</span>
              </div>
            </div>
          </div>

          {/* ขั้นตอนการคำนวณ */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-theme border border-gray-100 dark:border-slate-700 p-5 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-2">
                <FontAwesomeIcon icon={faEquals} className="text-violet-500" />
                ขั้นตอนการคำนวณ
              </h3>
              <button
                type="button"
                onClick={copySummary}
                className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 hover:bg-gray-50 transition-colors text-gray-600 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-slate-300 flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faCopy} /> คัดลอกสรุป
              </button>
            </div>

            <div className="space-y-2 text-sm font-mono tabular-nums overflow-x-auto">
              <div className="text-gray-600 dark:text-slate-300">
                1) รวมกระเป๋า = {fmt(calc.deposit)} + {fmt(calc.withdraw)} + {fmt(calc.safe)} + {fmt(calc.reseller)} ={' '}
                <strong className="text-blue-600 dark:text-blue-400">{fmt(calc.walletTotal)}</strong>
              </div>
              <div className="text-gray-600 dark:text-slate-300">
                2) สุทธิ = {fmt(calc.usdt)} − {fmt(calc.walletTotal)} ={' '}
                <strong className="text-emerald-600 dark:text-emerald-400">{fmt(calc.net)}</strong>
              </div>
              <div className="text-gray-600 dark:text-slate-300">
                3) ส่วนเจ้าของคงเหลือ = {fmt(calc.ownerFee)} − {fmt(calc.net)} ={' '}
                <strong className="text-teal-600 dark:text-teal-400">{fmt(calc.ownerRemain)}</strong>
              </div>
              <div className="text-gray-600 dark:text-slate-300">
                4) รวมคอม ={' '}
                <strong className="text-amber-600 dark:text-amber-400">{fmt(calc.commissionTotal)}</strong>
              </div>
              <div className="text-gray-800 dark:text-slate-100 pt-2 border-t border-gray-100 dark:border-slate-700">
                5) คงเหลือสุทธิ์ทั้งหมด = {fmt(calc.ownerRemain)} − {fmt(calc.commissionTotal)} ={' '}
                <strong className={calc.finalRemain >= 0 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}>
                  ฿{fmt(calc.finalRemain)}
                </strong>
              </div>
            </div>
          </div>
        </>
      )}

      {!loaded && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-theme border border-gray-100 dark:border-slate-700 p-10 text-center text-gray-400 dark:text-slate-500 text-sm">
          <FontAwesomeIcon icon={faCircleInfo} className="text-2xl mb-3 block mx-auto" />
          เลือกช่วงวันที่ของ Fee Report แล้วกด "ค้นหา" — ระบบจะดึงยอดกระเป๋าฝาก/ถอน/Safe, Wallet Reseller, Total USDT และส่วนเจ้าของมาคำนวณให้ทันที
        </div>
      )}
    </div>
  );
};

export default SettlementCalcPage;
